import { intersectionBy } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import type { IEpisodeCategoryBasic, IService, IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces/';
import { EpisodeCategory } from '@common/enums/episodeCategory';
import { translateKey } from '@frontend/translate';
import type { IServiceGroupWithProfiles } from '../../../../interfaces/organizationAddCarePathState';
import type { ISelectableOption } from '../../../../interfaces/selectableOption';
import type { ISelectableCard } from '../OrganizationAddCarePathContent';
import { mentalhealthServiceCodes } from '../constants/mentalhealthServiceCodes';
import { serviceGroupsByServiceCodes } from '../constants/serviceGroupsByServiceCodes';
import { OrganizationAddCarePathContent } from '../OrganizationAddCarePathContent';
import { OrganizationAddCarePathContext } from '../organizationAddCarePathContext';

interface IChooseCarePathProps {
    episodeCategory?: EpisodeCategory;
    serviceGroupMembershipsGroupedByEpisodeCategories: IServicesByServiceGroupForEpisodeCategory[];
}

export function ChooseCarePath(props: IChooseCarePathProps) {
    const { addCarePathState, dispatchAddCarePathAction } = useContext(OrganizationAddCarePathContext);
    const [cards, setCards] = useState<ISelectableCard[]>([]);

    useEffect(() => {
        if (props.serviceGroupMembershipsGroupedByEpisodeCategories.length > 0) {
            if (props.episodeCategory && !addCarePathState.episodeCategory) {
                setupEpisodeCategory(props.episodeCategory);
            }

            const createCards: ISelectableCard[] = [createFirstCard()];

            if (addCarePathState.episodeCategory?.id || props.episodeCategory) {
                const secondCard = createSecondCard(
                    addCarePathState.episodeCategory?.id ||
                        props.serviceGroupMembershipsGroupedByEpisodeCategories[0].episodeCategory.id
                );

                if (secondCard) {
                    createCards.push(secondCard);
                }
            }

            if (addCarePathState.episodeCategory?.id && addCarePathState.serviceGroups?.length) {
                const serviceCards = createServicesCards(addCarePathState.serviceGroups);

                if (serviceCards) {
                    createCards.push(...serviceCards);
                }
            }

            setCards(createCards);
        }
    }, [props.serviceGroupMembershipsGroupedByEpisodeCategories]);

    if (cards.length === 0) {
        return null;
    }

    return (
        <OrganizationAddCarePathContent
            cards={cards}
            hideEpisodeCategory={Boolean(props.episodeCategory)}
            onChange={onChange}
        />
    );

    function onChange(id: string, sequence: number) {
        switch (sequence) {
            case 0:
                handleSelectEpisodeCategory(id);
                break;
            case 1:
                handleSelectServiceGroup(id);
                break;
            case 2:
            case 3:
                handleSelectService(id, sequence);
                break;
            default:
                break;
        }
    }

    function handleSelectEpisodeCategory(id: string) {
        const updatedCard = cards[0];

        let secondCard: ISelectableCard | null = null;
        let selectedEpisodeCategory: ISelectableOption | undefined = undefined;
        updatedCard.selectableOptions = updatedCard.selectableOptions.map((option) => {
            if (option.id === id && !option.selected) {
                secondCard = createSecondCard(id);
                selectedEpisodeCategory = { ...option };
            }

            return {
                ...option,
                selected: option.id === id ? !option.selected : false,
            };
        });

        let episodeCategory: IEpisodeCategoryBasic | undefined = undefined;
        if (selectedEpisodeCategory) {
            const serviceGroupMembership = props.serviceGroupMembershipsGroupedByEpisodeCategories.find(
                (category) => category.episodeCategory.id === selectedEpisodeCategory!.id
            );
            episodeCategory = serviceGroupMembership?.episodeCategory;
        }

        if (secondCard && episodeCategory) {
            setCards([updatedCard, secondCard]);
        } else {
            setCards([updatedCard]);
        }

        dispatchAddCarePathAction({ type: 'setEpisodeCategory', episodeCategory });
    }

    function handleSelectServiceGroup(id: string) {
        const updatedCard = cards[1];
        const selectedServiceGroups: ISelectableOption[] = [];
        updatedCard.selectableOptions = updatedCard.selectableOptions.map((option) => {
            if ((option.id === id && !option.selected) || (option.id !== id && option.selected)) {
                selectedServiceGroups.push(option);
            }

            return {
                ...option,
                selected: option.id === id ? !option.selected : option.selected,
            };
        });

        let serviceCards: ISelectableCard[] = [];

        if (selectedServiceGroups.length > 0) {
            const serviceGroups = getServiceGroups(selectedServiceGroups);
            serviceCards = createServicesCards(serviceGroups);
            dispatchAddCarePathAction({ type: 'setServiceGroups', serviceGroups });
        } else {
            dispatchAddCarePathAction({ type: 'setServiceGroups', serviceGroups: undefined });
        }

        setCards([cards[0], updatedCard, ...serviceCards]);
    }

    function handleSelectService(id: string, sequence: number) {
        const updatedCards = [...cards];

        updatedCards[sequence].selectableOptions.forEach(
            (option) => (option.selected = option.id === id ? !option.selected : false)
        );
        setCards(updatedCards);

        const serviceCards = updatedCards.slice(2);

        serviceCards.forEach((serviceCard) =>
            dispatchAddCarePathAction({
                type: 'setServices',
                serviceGroupId: serviceCard.linkedServiceGroupId!,
                services: getSelectedServices(serviceCard),
                shouldResetServiceGroup: true,
            })
        );
    }

    function createFirstCard() {
        const selectableOptions: ISelectableOption[] = props.serviceGroupMembershipsGroupedByEpisodeCategories.map(
            (category) => ({
                name: category.episodeCategory.name,
                displayName: translateKey(`episodeCategoryEnum.${category.episodeCategory.name}`),
                id: category.episodeCategory.id,
                selected:
                    addCarePathState.episodeCategory?.id === category.episodeCategory.id ||
                    Boolean(props.episodeCategory),
            })
        );

        return {
            title: translateKey('carePath.whichCarePathToAdd'),
            sequence: 0,
            selectableOptions,
        };
    }

    function createSecondCard(id: string) {
        const serviceGroupMembershipsGroupedByEpisodeCategory =
            props.serviceGroupMembershipsGroupedByEpisodeCategories.find(
                (category) => category.episodeCategory.id === id
            );

        if (!serviceGroupMembershipsGroupedByEpisodeCategory) {
            return null;
        }

        const selectableOptions: ISelectableOption[] =
            serviceGroupMembershipsGroupedByEpisodeCategory.serviceGroups.map((serviceGroup) => ({
                name: serviceGroup.name,
                displayName: serviceGroup.name,
                id: serviceGroup.id,
                selected: Boolean(addCarePathState.serviceGroups?.some((item) => item.id === serviceGroup.id)),
            }));

        return {
            title: translateKey('carePath.whichSubcategoryToAdd'),
            subtitle: translateKey('carePath.multipleChoicesPossible'),
            sequence: 1,
            selectableOptions,
        };
    }

    function createServicesCards(selectedServiceGroups: IServiceGroupWithProfiles[]) {
        const serviceCards: ISelectableCard[] = [];
        const mentalHealthEpisodeCategory = props.serviceGroupMembershipsGroupedByEpisodeCategories.find(
            (serviceGroupMembership) => serviceGroupMembership.episodeCategory.name === EpisodeCategory.mentalHealth
        );

        const serviceGroupsWithSelectableService = mentalHealthEpisodeCategory?.serviceGroups.filter((serviceGroup) => {
            return serviceGroup.services?.some((service) => mentalhealthServiceCodes.includes(service.code));
        });

        const filteredSelectedServiceGroups = intersectionBy(
            serviceGroupsWithSelectableService,
            selectedServiceGroups,
            'id'
        );

        filteredSelectedServiceGroups.forEach((serviceGroup, index) => {
            const stateServiceGroup = selectedServiceGroups.find(
                (selectedServiceGroup) => selectedServiceGroup.id === serviceGroup.id
            );

            if (!serviceGroup.services || !stateServiceGroup) {
                return;
            }

            serviceCards.push({
                title: translateKey(`serviceGroup.${serviceGroupsByServiceCodes[serviceGroup.services[0].code]}`),
                sequence: 2 + index,
                selectableOptions: serviceGroup.services
                    .filter((service) => mentalhealthServiceCodes.includes(service.code))
                    .map((service) => ({
                        name: service.name,
                        id: service.id,
                        selected: Boolean(stateServiceGroup.services?.some((item) => item.id === service.id)),
                        displayName: translateKey(`service.${service.code}`),
                    })),
                linkedServiceGroupId: stateServiceGroup.id,
            });
        });

        return serviceCards;
    }

    function getServiceGroups(selectedServiceGroups: ISelectableOption[]): IServiceGroupWithProfiles[] {
        const serviceGroupMembership = props.serviceGroupMembershipsGroupedByEpisodeCategories.find(
            (groupedServiceGroupMembership) =>
                groupedServiceGroupMembership.episodeCategory.name === addCarePathState.episodeCategory?.name
        );

        const serviceGroups = serviceGroupMembership?.serviceGroups;
        if (!serviceGroups) {
            return [];
        }

        const result: IServiceGroupWithProfiles[] = [];
        for (const selectedServiceGroup of selectedServiceGroups) {
            const serviceGroup = serviceGroups.find((group) => group.id === selectedServiceGroup.id);
            if (!serviceGroup) {
                continue;
            }
            const existingServiceGroup = addCarePathState.serviceGroups?.find(
                ({ id }) => id === selectedServiceGroup.id
            );
            if (existingServiceGroup) {
                result.push(existingServiceGroup);
            } else {
                const services = serviceGroup.services?.filter(
                    (service) => !mentalhealthServiceCodes.includes(service.code)
                );

                const resultServiceGroup: IServiceGroupWithProfiles = {
                    ...selectedServiceGroup,
                    services,
                };

                result.push(resultServiceGroup);
            }
        }

        return result;
    }

    function getSelectedServices(serviceCard: ISelectableCard) {
        const services: IService[] = [];

        const serviceGroupMembership = props.serviceGroupMembershipsGroupedByEpisodeCategories.find(
            (groupedServiceGroupMembership) =>
                groupedServiceGroupMembership.episodeCategory.name === addCarePathState.episodeCategory?.name
        );

        const serviceGroup = serviceGroupMembership?.serviceGroups.find(
            (group) => group.id === serviceCard.linkedServiceGroupId
        );
        if (serviceGroup && serviceGroup.services) {
            services.push(
                ...serviceGroup.services.filter((service) => !mentalhealthServiceCodes.includes(service.code))
            );
            for (const option of serviceCard.selectableOptions) {
                if (option.selected) {
                    const service = serviceGroup.services.find((s) => s.id === option.id);
                    if (service) {
                        services.push(service);
                    }
                }
            }
        }

        return services;
    }

    function setupEpisodeCategory(episodeCategoryName: EpisodeCategory) {
        const serviceGroupMembership = props.serviceGroupMembershipsGroupedByEpisodeCategories.find(
            (category) => category.episodeCategory.name === episodeCategoryName
        );
        const episodeCategory = serviceGroupMembership?.episodeCategory;
        dispatchAddCarePathAction({ type: 'setEpisodeCategory', episodeCategory });
    }
}
